import { NON_INTERNATIONAL_COUNTRIES_ISO } from "@constants/country";

import { isCustomCurrency } from "@stadium/helpers/currency";

export const convertPrice = (
  { from, to, value, conversionRates },
  { ceilRequired = false, ceilRequiredBeforeConversion = false } = {},
) => {
  const priceValue = ceilRequiredBeforeConversion ? Math.ceil(value.toFixed(4)) : value;

  const amountInBaseCurrency = parseFloat(
    from && to
      ? (priceValue / conversionRates[from.toLowerCase()]) * conversionRates[to.toLowerCase()]
      : priceValue,
  );
  if (ceilRequired) return Math.ceil(amountInBaseCurrency.toFixed(4));
  return parseFloat(amountInBaseCurrency.toFixed(2));
};

export const formatPrice = (
  number,
  { prefix = "", suffix = "", currencyCode = "", currencyDisplay, ceilRequired = false } = {},
) => {
  if (ceilRequired) number = Math.ceil(parseFloat(number).toFixed(4));
  number = parseFloat(number).toFixed(2);
  const fractionDigits = number % 1 == 0 ? 0 : 2;
  const fractionDigitsParams = !fractionDigits
    ? {
        maximumFractionDigits: fractionDigits,
        minimumFractionDigits: fractionDigits,
      }
    : {};
  if (isCustomCurrency(currencyCode) || !currencyCode) {
    const currencyFormatter = new Intl.NumberFormat(undefined, {
      ...fractionDigitsParams,
      ...(currencyDisplay && { currencyDisplay }),
    });
    const displayPrice = currencyFormatter.format(number);
    return `${prefix}${displayPrice} ${suffix}`.trim();
  } else {
    const currencyFormatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      ...fractionDigitsParams,
      ...(currencyDisplay && { currencyDisplay }),
    });
    return currencyFormatter.format(number);
  }
};

export const getCurrencySymbol = (currencyCode) => {
  if (!currencyCode || isCustomCurrency(currencyCode)) return "";
  try {
    const parts = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
    }).formatToParts(0);
    const symbolPart = parts.find((part) => part.type === "currency");
    return symbolPart ? symbolPart.value : "";
  } catch (e) {
    /* eslint-disable-next-line no-console */
    console.error("Error getting currency symbol:", e);
    return "";
  }
};

export const formatNumberToShortScale = (number) => {
  if (!number) return "0";
  const suffixes = ["", "K", "M", "B", "T"];
  const suffixIndex = Math.floor(Math.log10(number) / 3);
  const formattedInteger = number / Math.pow(10, suffixIndex * 3);
  const formattedNumber = formattedInteger.toFixed(Number.isInteger(formattedInteger) ? 0 : 1);

  return formattedNumber + suffixes[suffixIndex];
};

export const getFormattedOrderCheckoutSummary = (
  points,
  conversionRate,
  storeCurrencyConversionRate,
  stadiumWalletBalanceInUsd,
  selectedTeamWalletId,
  taxTotal,
) => {
  const priceInUsd = points / storeCurrencyConversionRate;
  const computedAmount = (priceInUsd + taxTotal) * conversionRate;
  const computedstadiumWalletBalance = stadiumWalletBalanceInUsd * conversionRate;
  const youPayAmount =
    computedAmount > computedstadiumWalletBalance && !selectedTeamWalletId
      ? computedAmount - computedstadiumWalletBalance
      : 0;

  const youPayInUSD = youPayAmount / conversionRate;
  const youPayInStoreCurrency = youPayInUSD * storeCurrencyConversionRate;

  return {
    currencyAmount: parseFloat(youPayAmount.toFixed(2)),
    cartTotalInSelectedCurrency: computedAmount,
    walletBalanceInUse: Math.min(computedAmount, computedstadiumWalletBalance),
    youPayInStoreCurrency: parseFloat(youPayInStoreCurrency.toFixed(2)),
  };
};

export const getCurrencyCode = (currencies, currencyId) =>
  currencies.find((elmt) => elmt.id === currencyId)?.code?.toLowerCase();

const isInternational = (country) => !NON_INTERNATIONAL_COUNTRIES_ISO.includes(country.iso);

export const getCountriesWithInternationalData = (configData) => {
  const { countries } = configData;

  const updatedCountries = countries?.map((country) => ({
    ...country,
    international: isInternational(country),
  }));

  return { ...configData, countries: updatedCountries };
};
